import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Spinner, Container } from 'react-bootstrap';

// Import global styles
import './styles/global.css'; 

// Import components and pages
import AppNavbar from './components/AppNavbar';
import { AuthProvider } from './contexts/AuthContext'; 
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute'; 
import AppFooter from './components/AppFooter'; 

// Lazy Load Pages
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AutomationStudioPage = lazy(() => import('./pages/AutomationStudioPage'));
const AutomationRunDetailPage = lazy(() => import('./pages/AutomationRunDetailPage'));
const CreateProjectPage = lazy(() => import('./pages/CreateProjectPage'));
const ProjectViewPage = lazy(() => import('./pages/ProjectViewPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage')); 

// Info Pages
const BusinessRoadmapPage = lazy(() => import('./pages/BusinessRoadmapPage'));
const FeatureRoadmapPage = lazy(() => import('./pages/FeatureRoadmapPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const IDEView = lazy(() => import('./pages/IDEView')); 
const AdminPage = lazy(() => import('./pages/AdminPage')); 

// Loading Fallback Component
const PageLoadingFallback = () => (
  <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
    <Spinner animation="border" variant="primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </Container>
);

function App() {
  return (
    <Router>
      <AuthProvider> 
        <ThemeProvider> {/* <-- 2. WRAP APP WITH THEME PROVIDER */}
          <div className="d-flex flex-column min-vh-100">
            <AppNavbar />
            <main className="flex-grow-1 py-4 mt-5"> 
              <Suspense fallback={<PageLoadingFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />

                  {/* Public Info Pages */}
                  <Route path="/business-roadmap" element={<BusinessRoadmapPage />} />
                  <Route path="/feature-roadmap" element={<FeatureRoadmapPage />} />
                  <Route path="/changelog" element={<ChangelogPage />} />
                  <Route path="/team" element={<TeamPage />} />

                  {/* Protected Routes */}
                  <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                  <Route path="/automation" element={<ProtectedRoute><AutomationStudioPage /></ProtectedRoute>} />
                  <Route path="/automation/runs/:runId" element={<ProtectedRoute><AutomationRunDetailPage /></ProtectedRoute>} />
                  <Route path="/projects/new" element={<ProtectedRoute><CreateProjectPage /></ProtectedRoute>} />
                  <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectViewPage /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                  <Route path="/subscribe" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
                  <Route path="/ide" element={<ProtectedRoute><IDEView /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                  {/* Add more routes as needed */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </main>
            <AppFooter /> 
          </div>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
